import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, UserPlus, Sparkles, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Candidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_primary: string | null;
  city: string | null;
  ai_skills_summary: string | null;
}

interface AddCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  existingCandidateIds: string[];
  onAddCandidate: (candidateId: string) => Promise<boolean>;
}

export function AddCandidateDialog({
  open,
  onOpenChange,
  jobId,
  existingCandidateIds,
  onAddCandidate,
}: AddCandidateDialogProps) {
  const [search, setSearch] = useState('');
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<Candidate[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) {
      fetchCandidates();
      fetchAiSuggestions();
    }
  }, [open]);

  const fetchCandidates = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('candidates')
      .select('id, first_name, last_name, email_primary, city, ai_skills_summary')
      .order('created_at', { ascending: false })
      .limit(100);

    if (!error && data) {
      setCandidates(data);
    }
    setLoading(false);
  };

  const fetchAiSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      // Get job details for matching
      const { data: job } = await supabase
        .from('jobs')
        .select(`
          title,
          description,
          job_skills (
            skills (name)
          )
        `)
        .eq('id', jobId)
        .single();

      if (!job) return;

      const jobSkills = job.job_skills?.map((js: any) => js.skills?.name).filter(Boolean) || [];

      // Get candidates with matching skills
      const { data: matchingCandidates } = await supabase
        .from('candidates')
        .select(`
          id,
          first_name,
          last_name,
          email_primary,
          city,
          ai_skills_summary,
          candidate_skills (
            skills (name)
          )
        `)
        .not('id', 'in', `(${existingCandidateIds.length > 0 ? existingCandidateIds.join(',') : 'null'})`)
        .limit(50);

      if (matchingCandidates) {
        // Score candidates by skill match
        const scored = matchingCandidates.map((c: any) => {
          const candidateSkills = c.candidate_skills?.map((cs: any) => cs.skills?.name?.toLowerCase()).filter(Boolean) || [];
          const matchCount = jobSkills.filter((js: string) => 
            candidateSkills.some((cs: string) => cs.includes(js.toLowerCase()) || js.toLowerCase().includes(cs))
          ).length;
          return { ...c, matchScore: matchCount };
        });

        // Sort by match score and take top 5
        scored.sort((a: any, b: any) => b.matchScore - a.matchScore);
        setAiSuggestions(scored.slice(0, 5).filter((c: any) => c.matchScore > 0));
      }
    } catch (error) {
      console.error('Error fetching AI suggestions:', error);
    }
    setLoadingSuggestions(false);
  };

  const handleAddCandidate = async (candidateId: string) => {
    setAdding(candidateId);
    const success = await onAddCandidate(candidateId);
    if (success) {
      // Remove from local lists
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      setAiSuggestions(prev => prev.filter(c => c.id !== candidateId));
    }
    setAdding(null);
  };

  const filteredCandidates = candidates.filter(c => {
    if (existingCandidateIds.includes(c.id)) return false;
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(searchLower) ||
      c.last_name?.toLowerCase().includes(searchLower) ||
      c.email_primary?.toLowerCase().includes(searchLower) ||
      c.city?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Add Candidate to Job</DialogTitle>
          <DialogDescription>
            Search for existing candidates or view AI-suggested matches
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* AI Suggestions */}
          {(loadingSuggestions || aiSuggestions.length > 0) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Sparkles className="h-4 w-4 text-primary" />
                AI Suggested Matches
              </div>
              {loadingSuggestions ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Finding best matches...
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {aiSuggestions.map(candidate => (
                    <Badge
                      key={candidate.id}
                      variant="secondary"
                      className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors py-2 px-3"
                      onClick={() => handleAddCandidate(candidate.id)}
                    >
                      {adding === candidate.id ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <UserPlus className="h-3 w-3 mr-1" />
                      )}
                      {candidate.first_name} {candidate.last_name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search candidates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Candidates List */}
          <ScrollArea className="h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : filteredCandidates.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No candidates found
              </div>
            ) : (
              <div className="space-y-2">
                {filteredCandidates.map(candidate => (
                  <div
                    key={candidate.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">
                        {candidate.first_name} {candidate.last_name}
                      </div>
                      {candidate.email_primary && (
                        <div className="text-sm text-muted-foreground truncate">
                          {candidate.email_primary}
                        </div>
                      )}
                      {candidate.city && (
                        <div className="text-sm text-muted-foreground">
                          {candidate.city}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={adding === candidate.id}
                      onClick={() => handleAddCandidate(candidate.id)}
                    >
                      {adding === candidate.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
